import { countFilterItemsForDisplay } from 'components/QFilterSelect/utils';
import type { RecordOf as ImRecordOf } from 'immutable';
import { Set as ImSet } from 'immutable';

import type {
  AdvancedFilter,
  CustomerAndProjects,
  FilterObject,
  FilterObjectProps,
} from 'app-components/flux/filters/filtersTypes';
import { AdvancedFilterObject, mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import { CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE } from 'components/QFilterSelect/constants';
import { DateTime } from 'luxon';

// calculating this once to be used later
const advancedFilterObjectKeys = AdvancedFilterObject()
  .toSeq()
  .toArray()
  .map(([key]) => key);
const filterObjectKeys = mkFilterObject()
  .toSeq()
  .toArray()
  .map(([key]) => key);

let meaningFulSearchParams;

const serializeBoolean = (boolean: boolean) => `${Number(boolean)}`;

const deserializeBoolean = (string: string) => Boolean(parseInt(string, 2));

const serializeBooleanParam = (key: string, value: boolean | undefined, searchParams: URLSearchParams) => {
  if (value === undefined) {
    searchParams.delete(key);
  } else {
    searchParams.set(key, serializeBoolean(value));
  }
};

const deserializeBooleanParam = (
  key: string,
  searchParams: URLSearchParams,
  defaultValue: undefined | boolean = undefined,
) => {
  const value: null | string = searchParams.get(key);

  if (value === '0' || value === '1') {
    meaningFulSearchParams = true;
    return deserializeBoolean(value);
  }

  return defaultValue;
};

const serializeSet = (set: ImSet<string>) => set.toIndexedSeq().toArray().join(',');

const deserializeSet = (string: string): ImSet<string> => ImSet(string.split(','));

const serializeSetParam = (key: string, value: ImSet<string> | undefined, searchParams: URLSearchParams) => {
  if (value?.size) {
    searchParams.set(key, serializeSet(value));
  } else {
    searchParams.delete(key);
  }
};

const deserializeSetParam = (key: string, searchParams: URLSearchParams) => {
  const value = searchParams.get(key);

  if (value) {
    meaningFulSearchParams = true;
    return deserializeSet(value);
  }

  return ImSet<string>();
};

const serializeDateParam = (
  key: string,
  value: DateTime | string | undefined | null,
  searchParams: URLSearchParams,
) => {
  if (!value) {
    searchParams.delete(key);
    return;
  }
  const dt = typeof value === 'string' ? DateTime.fromISO(value) : value;
  const iso = dt.isValid ? dt.toISODate() : null;
  if (iso) {
    searchParams.set(key, iso);
  } else {
    searchParams.delete(key);
  }
};

type DateBoundary = 'startOfDay' | 'endOfDay';
const deserializeDateParam = (
  key: string,
  searchParams: URLSearchParams,
  boundary: DateBoundary,
): DateTime | undefined => {
  const raw = searchParams.get(key);
  if (!raw) return undefined;
  const parsed = DateTime.fromISO(raw);
  if (!parsed.isValid) return undefined;
  meaningFulSearchParams = true;
  const expanded = boundary === 'endOfDay' ? parsed.endOf('day') : parsed.startOf('day');
  return expanded ?? parsed;
};

type AdvancedFilterObjectKeySerializer = (
  key: keyof AdvancedFilter,
  value: undefined | boolean,
  searchParams: URLSearchParams,
) => void;
const advancedFilterObjectKeySerializer: AdvancedFilterObjectKeySerializer = (key, value, searchParams) => {
  switch (key) {
    case 'isBillOrSubscription':
    case 'isExcludedFromReports':
    case 'isReviewed':
    case 'isExcludedFromF2S':
      serializeBooleanParam(key, value, searchParams);
      return;
  }
};

function filterObjectKeySerializer(
  key: keyof FilterObjectProps,
  value:
    | string
    | number
    | boolean
    | ImSet<string>
    | ImRecordOf<AdvancedFilter>
    | ImSet<CustomerAndProjects>
    | DateTime<boolean>
    | undefined,
  searchParams: URLSearchParams,
) {
  switch (key) {
    case 'categories':
    case 'businesses':
    case CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE:
    case 'customersAndProjectsSelected':
    case 'payees':
    case 'tags':
    case 'accounts':
    case 'payeeNames':
    case 'flags':
      serializeSetParam(key, value as ImSet<string>, searchParams);
      return;
    case 'advanced':
      (value as ImRecordOf<AdvancedFilter>).toSeq().forEach((value, innerKey) => {
        advancedFilterObjectKeySerializer(innerKey, value, searchParams);
      });
      return;
    case 'filterOut':
      serializeBooleanParam(key, value as boolean | undefined, searchParams);
      return;
    case 'dateStart':
    case 'dateEnd':
      serializeDateParam(key, value as DateTime | string | undefined | null, searchParams);
      return;
    // ignore the following keys
    case 'numFilterItems':
      return;
  }
}

const advancedFilterObjectKeyDeserializer = (key: keyof AdvancedFilter, searchParams: URLSearchParams) => {
  switch (key) {
    case 'isBillOrSubscription':
    case 'isExcludedFromReports':
    case 'isReviewed':
    case 'isExcludedFromF2S':
      return deserializeBooleanParam(key, searchParams);
    default:
      return null;
  }
};

const filterObjectKeyDeserializer = (key: keyof FilterObjectProps, searchParams: URLSearchParams) => {
  const advancedFilter = {};
  switch (key) {
    case 'categories':
    case 'businesses':
    case CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE:
    case 'customersAndProjectsSelected':
    case 'payees':
    case 'tags':
    case 'accounts':
    case 'payeeNames':
    case 'flags':
      return deserializeSetParam(key, searchParams);
    case 'advanced':
      advancedFilterObjectKeys.forEach((advKey) => {
        // @ts-expect-error FIXME TS7053
        advancedFilter[advKey] = advancedFilterObjectKeyDeserializer(advKey, searchParams);
      });

      return AdvancedFilterObject(advancedFilter);
    case 'filterOut':
      return deserializeBooleanParam(key, searchParams, false);
    case 'dateStart':
      return deserializeDateParam(key, searchParams, 'startOfDay');
    case 'dateEnd':
      return deserializeDateParam(key, searchParams, 'endOfDay');
    // ignore the following keys
    case 'numFilterItems':
      return 0;
    default:
      return null;
  }
};

type DeserializeOptions = {
  /**
   * When false (default), `dateStart` / `dateEnd` URL params are ignored.
   * Callers that have opted into the date-range filter set this true after checking
   * the `registerToolbarDatePicker` feature flag.
   */
  allowDateRange?: boolean;
};

export const serializeFilterObjectToSearchParams = (searchParams: URLSearchParams, filterObject: FilterObject) => {
  filterObject.toSeq().forEach((value, key) => {
    // Date keys must be processed even when falsy so a cleared range removes its URL param.
    // The serializer's null/undefined branches handle the deletion safely.
    if (value || key === 'dateStart' || key === 'dateEnd') {
      filterObjectKeySerializer(key, value ?? undefined, searchParams);
    }
  });
};

export const deserializeFilterObjectFromSearchParams = (
  searchParams: URLSearchParams,
  { allowDateRange = false }: DeserializeOptions = {},
) => {
  const filterObject = {};
  meaningFulSearchParams = false;

  filterObjectKeys.forEach((key) => {
    if (!allowDateRange && (key === 'dateStart' || key === 'dateEnd')) return;
    // @ts-expect-error FIXME TS7053
    filterObject[key] = filterObjectKeyDeserializer(key, searchParams);
  });

  if (meaningFulSearchParams) {
    const filterRecord = mkFilterObject(filterObject);
    // Need to include dates in the filter count, EXCEPT for the legacy filter, which must maintain its own count
    // TODO: refactor for sustainability
    const baseCount = countFilterItemsForDisplay(filterRecord, { includeBusinesses: true });
    const dateCount = filterRecord.dateStart || filterRecord.dateEnd ? 1 : 0;
    return filterRecord.set('numFilterItems', baseCount + dateCount);
  }

  return null;
};

// @ts-expect-error FIXME TS7006
export const clearFilterObjectSearchParams = (searchParams) => {
  filterObjectKeys.forEach((key) => {
    searchParams.delete(key);
  });
  advancedFilterObjectKeys.forEach((key) => {
    searchParams.delete(key);
  });
};
